import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const steps = [
  {
    n: "01",
    title: "Skicka din förfrågan",
    desc: "Välj vad bilen behöver och ange ett datum som passar dig.",
  },
  {
    n: "02",
    title: "Vi stämmer av",
    desc: "ZAM återkommer med tid och går igenom vad som ska göras innan besöket.",
  },
  {
    n: "03",
    title: "Bilen tas om hand",
    desc: "Arbetet utförs och du hålls uppdaterad om något behöver ändras på vägen.",
  },
  {
    n: "04",
    title: "Klart att köra",
    desc: "Du får tillbaka bilen med en tydlig genomgång av vad som har gjorts.",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 70%", "end 60%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="process"
      ref={ref}
      className="relative py-24 lg:py-36 bg-[#F8FAFC] overflow-hidden"
    >
      <div className="max-w-[1360px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-6 mb-14 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">02 — Så fungerar det</span>
            <h2 className="hero-text mt-5 text-[9vw] sm:text-[6vw] lg:text-[4.4vw] xl:text-[72px]">
              Fyra tydliga steg<br />
              <span className="italic text-[#D52027]">från fråga till färdig bil.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 text-slate-600 text-[1.02rem] leading-relaxed max-w-md"
          >
            Det ska vara enkelt att lämna in bilen. Du vet vad nästa steg är, vad som
            händer och när det är dags att köra vidare.
          </motion.p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
          {/* Progress line */}
          <div className="hidden lg:block absolute top-[52px] left-0 right-0 h-[2px] bg-slate-200">
            <motion.div
              style={{ scaleX: lineScale, transformOrigin: "0% 50%" }}
              className="h-full bg-gradient-to-r from-[#1F3C88] via-[#1F3C88] to-[#D52027]"
            />
          </div>

          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="relative"
            >
              <div className="flex items-center gap-3">
                <div className="h-[52px] w-[52px] rounded-full bg-white border border-slate-200 grid place-items-center shadow-sm relative z-10">
                  <span className="font-mono text-xs font-bold text-[#1F3C88]">
                    {s.n}
                  </span>
                </div>
                <div className="font-mono text-[0.65rem] tracking-[0.2em] uppercase text-slate-400">
                  Steg {s.n}
                </div>
              </div>
              <h3 className="mt-6 font-display text-[1.8rem] lg:text-[2rem] text-slate-900 leading-[1.05] tracking-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-slate-600 text-[0.95rem] leading-relaxed">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
